import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import type { SignalingGenerationV1 } from '../types/messages/peer';

export enum PeerMessageType {
  PEER_GET_ID = 'PEER_GET_ID',
  PEER_GET_ID_ANSWER = 'PEER_GET_ID_ANSWER',
  PEER_SDP_OFFER = 'PEER_SDP_OFFER',
  PEER_SDP_ANSWER = 'PEER_SDP_ANSWER',
  PEER_ICE_CANDIDATE = 'PEER_ICE_CANDIDATE',
  PEER_ICE_COMPLETE = 'PEER_ICE_COMPLETE',
  PEER_STATS = 'PEER_STATS',
  PEER_STATS_SUCCESS = 'PEER_STATS_SUCCESS',
  PEER_ERROR = 'PEER_ERROR',
}

export enum PeerIdType {
  REMOTE_HOST = 'remoteHost',
  REMOTE_CLIENT = 'remoteClient',
  HOST = 'host',
  PLAYER = 'player',
  ADMIN = 'admin',
}

export const DEVICE_CLASSIFICATION_VERSION = 1 as const;
export const SIGNALING_GENERATION_VERSION = 1 as const;

export const signalingGenerationV1Schema = z.object({
  version: z.literal(SIGNALING_GENERATION_VERSION),
  sessionId: z.string().min(1).max(256).refine((value) => value.trim().length > 0, {
    message: 'sessionId must contain a non-whitespace character',
  }),
  epoch: z.number().int().nonnegative(),
}) as z.ZodType<SignalingGenerationV1, z.ZodTypeDef, unknown>;

const peerMetadataShape = {
  /**
   * Marks metadata written by a client that computes its device classification before any
   * optional probe. Its presence is what makes `isMobile` mandatory; its absence identifies
   * a cached legacy client, which stays valid so a rolling deploy keeps working.
   */
  deviceClassificationVersion: z.literal(DEVICE_CLASSIFICATION_VERSION).optional(),
  signalingGenerationVersion: z.literal(SIGNALING_GENERATION_VERSION).optional(),
  isMobile: z.boolean().optional(),
  isApple: z.boolean().optional(),
  isFirefox: z.boolean().optional(),
  screenWidth: z.number().optional(),
  screenHeight: z.number().optional(),
  displayName: z.string().optional(),
  /** Guest-computed (Phase 1 isQuestionableConnection). Host uses for initial Cam only. */
  connectionQuestionable: z.boolean().optional(),
  /**
   * Video families the GUEST can decode in HARDWARE, lower-case family names
   * ('h264' | 'h265' | 'av1' | 'vp8' | 'vp9'). Absent means "unknown" - the host
   * must then behave exactly as it did before this field existed.
   */
  hardwareDecodeFamilies: z.array(z.string()).optional(),
  /**
   * Origin of an admin peer session. Lets the host apply a non-destructive
   * disconnect default (e.g. macro-recorder sessions restart the game and keep
   * Nonsole Mode instead of exiting guest mode on an abrupt drop/reload).
   */
  purpose: z.enum(['macroRecorder']).optional(),
  /**
   * Redis admin-session reservation UUID. Host presence republishes this so
   * force-recovery / orphan clear can match the exact live session identity.
   */
  adminSessionId: z.string().uuid().optional(),
};

type PeerMetadataShape = typeof peerMetadataShape;

/**
 * Legacy client field names → current schema names, applied before validation.
 *
 * The peer library (libs/Peer) publishes `isMobileDevice`/`isAppleDevice`/`isFirefoxBrowser`
 * and the production client (apps/marketing) sends those through PEER_GET_ID. Zod strips
 * unknown keys, so without this mapping every production-client guest reached the host with
 * NO isMobile — and `selectInitialQualityTier` reads absent as desktop, seating an iPhone on
 * the 720p capture. Aliases are only read when the current name is absent, so a client that
 * already speaks the current shape is untouched.
 */
const LEGACY_METADATA_ALIASES: ReadonlyArray<readonly [legacy: string, current: string]> = [
  ['isMobileDevice', 'isMobile'],
  ['isAppleDevice', 'isApple'],
  ['isFirefoxBrowser', 'isFirefox'],
];

export function normalizeLegacyPeerMetadata<T extends Record<string, unknown>>(metadata: T): T {
  const out: Record<string, unknown> = { ...metadata };
  for (const [legacy, current] of LEGACY_METADATA_ALIASES) {
    if (out[current] === undefined && out[legacy] !== undefined) {
      out[current] = out[legacy];
    }
    delete out[legacy];
  }
  return out as T;
}

/**
 * Object schema that carries the device-classification rule through `.extend()`.
 *
 * The rule is cross-field -- `deviceClassificationVersion: 1` makes `isMobile` mandatory --
 * and expressing it with `.superRefine()` produces a `ZodEffects`, which has no `.extend()`.
 * The beacon fast-start payload does extend this schema (to bound `displayName`), so the rule
 * lives in the object type itself: every extension is still a real `ZodObject` and still
 * rejects a marked payload with no boolean `isMobile`.
 */
export class PeerMetadataObject<Shape extends z.ZodRawShape> extends z.ZodObject<Shape> {
  override _parse(input: z.ParseInput): z.ParseReturnType<this['_output']> {
    // Normalize legacy client field names before validation, so every parse site (PEER_GET_ID,
    // SDP offer envelopes, fast-start payloads) maps `isMobileDevice` → `isMobile` etc. without
    // each handler knowing the legacy shapes.
    if (input.data && typeof input.data === 'object' && !Array.isArray(input.data)) {
      input.data = normalizeLegacyPeerMetadata(input.data as Record<string, unknown>);
    }
    const parsed = super._parse(input);
    return parsed instanceof Promise
      ? parsed.then((result) => this.requireCurrentClassification(input, result))
      : this.requireCurrentClassification(input, parsed);
  }

  override extend<Augmentation extends z.ZodRawShape>(
    augmentation: Augmentation,
  ): z.ZodObject<z.objectUtil.extendShape<Shape, Augmentation>> {
    return new PeerMetadataObject(super.extend(augmentation)._def);
  }

  private requireCurrentClassification(
    input: z.ParseInput,
    result: z.SyncParseReturnType<this['_output']>,
  ): z.SyncParseReturnType<this['_output']> {
    if (result.status === 'aborted') return result;

    const metadata = result.value as {
      deviceClassificationVersion?: unknown;
      isMobile?: unknown;
    };
    if (
      metadata.deviceClassificationVersion === DEVICE_CLASSIFICATION_VERSION &&
      typeof metadata.isMobile !== 'boolean'
    ) {
      z.addIssueToContext(this._getOrReturnCtx(input), {
        code: z.ZodIssueCode.custom,
        path: ['isMobile'],
        message: 'isMobile is required for current device-classification metadata',
      });
      return z.INVALID;
    }
    return result;
  }
}

const createPeerMetadataObject = <Shape extends z.ZodRawShape>(shape: Shape): PeerMetadataObject<Shape> =>
  new PeerMetadataObject(z.object(shape)._def);

/**
 * Device and identity metadata sent by the client when requesting a peer ID.
 * Stored server-side and proxied to the host alongside SDP messages so that
 * the host can configure the connection (track quality, permissions, etc.)
 * *before* the SDP answer -- eliminating the need for post-connect handshake.
 */
export const peerMetadataSchema: PeerMetadataObject<PeerMetadataShape> =
  createPeerMetadataObject(peerMetadataShape);
export type PeerMetadata = z.infer<typeof peerMetadataSchema>;

export type SdpPayload = z.infer<typeof sdpSchema>;
export type IceCandidatePayload = z.infer<typeof iceCandidateSchema>;

// Common schemas for WebRTC data
export const sdpSchema = z.object({
  type: z.enum(['offer', 'answer', 'pranswer', 'rollback']),
  sdp: z.string(),
});

// from RTCIceCandidate
const iceCandidateSchema = z.object({
  json: z.array(z.string()),
});

// Stats schema (can be expanded based on what stats you want to collect)
const rtcStatsSchema = z.object({
  timestamp: z.number(),
  type: z.string(),
  bitrate: z.number().optional(),
  packetLoss: z.number().optional(),
  roundTripTime: z.number().optional(),
  // Add other stats as needed
});

// Payload schemas
const peerGetIdPayloadSchema = z.object({
  peerIdType: z.nativeEnum(PeerIdType),
  peerId: z.string().optional(),
  peerToken: z.string().optional(),
  peerMetadata: peerMetadataSchema.optional(),
});

const peerGetIdAnswerPayloadSchema = z.object({
  peerId: z.string(),
  peerIdType: z.nativeEnum(PeerIdType),
  peerToken: z.string().optional(),
  iceServers: z
    .object({
      urls: z.array(z.string()),
      username: z.string(),
      credential: z.string(),
    })
    .optional(),
});

const peerSdpOfferPayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  sdp: sdpSchema,
  peerMetadata: peerMetadataSchema.optional(),
  peerIdType: z.nativeEnum(PeerIdType).optional(),
  connectionGeneration: signalingGenerationV1Schema.optional(),
});

const peerSdpAnswerPayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  sdp: sdpSchema,
  connectionGeneration: signalingGenerationV1Schema.optional(),
});

const peerIceCandidatePayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  candidates: iceCandidateSchema,
  connectionGeneration: signalingGenerationV1Schema.optional(),
});

const peerIceCompletePayloadSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  connectionGeneration: signalingGenerationV1Schema.optional(),
});

const peerStatsPayloadSchema = z.object({
  peerId: z.string(),
  stats: rtcStatsSchema,
});

const peerStatsSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const peerErrorPayloadSchema = z.object({
  peerId: z.string(),
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

// Full message schemas
const peerGetIdSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_GET_ID),
  })
  .merge(peerGetIdPayloadSchema);

const peerGetIdAnswerSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_GET_ID_ANSWER),
  })
  .merge(peerGetIdAnswerPayloadSchema);

const peerSdpOfferSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_SDP_OFFER),
  })
  .merge(peerSdpOfferPayloadSchema);

const peerSdpAnswerSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_SDP_ANSWER),
  })
  .merge(peerSdpAnswerPayloadSchema);

const peerIceCandidateSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_ICE_CANDIDATE),
  })
  .merge(peerIceCandidatePayloadSchema);

const peerIceCompleteSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_ICE_COMPLETE),
  })
  .merge(peerIceCompletePayloadSchema);

const peerStatsSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_STATS),
  })
  .merge(peerStatsPayloadSchema);

const peerStatsSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_STATS_SUCCESS),
  })
  .merge(peerStatsSuccessPayloadSchema);

const peerErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(PeerMessageType.PEER_ERROR),
  })
  .merge(peerErrorPayloadSchema);

export const peerMessageSchema = z.discriminatedUnion('type', [
  peerGetIdSchema,
  peerGetIdAnswerSchema,
  peerSdpOfferSchema,
  peerSdpAnswerSchema,
  peerIceCandidateSchema,
  peerIceCompleteSchema,
  peerStatsSchema,
  peerStatsSuccessSchema,
  peerErrorSchema,
]);

// Export payload types
export type PeerGetIdPayload = z.infer<typeof peerGetIdPayloadSchema>;
export type PeerGetIdAnswerPayload = z.infer<typeof peerGetIdAnswerPayloadSchema>;
export type PeerSdpOfferPayload = z.infer<typeof peerSdpOfferPayloadSchema>;
export type PeerSdpAnswerPayload = z.infer<typeof peerSdpAnswerPayloadSchema>;
export type PeerIceCandidatePayload = z.infer<typeof peerIceCandidatePayloadSchema>;
export type PeerIceCompletePayload = z.infer<typeof peerIceCompletePayloadSchema>;
export type PeerStatsPayload = z.infer<typeof peerStatsPayloadSchema>;
export type PeerStatsSuccessPayload = z.infer<typeof peerStatsSuccessPayloadSchema>;
export type PeerErrorPayload = z.infer<typeof peerErrorPayloadSchema>;

// Full message type
export type PeerMessage = z.infer<typeof peerMessageSchema>;

type PayloadType<T extends PeerMessageType> = T extends PeerMessageType.PEER_GET_ID
  ? PeerGetIdPayload
  : T extends PeerMessageType.PEER_GET_ID_ANSWER
    ? PeerGetIdAnswerPayload
    : T extends PeerMessageType.PEER_SDP_OFFER
      ? PeerSdpOfferPayload
      : T extends PeerMessageType.PEER_SDP_ANSWER
        ? PeerSdpAnswerPayload
        : T extends PeerMessageType.PEER_ICE_CANDIDATE
          ? PeerIceCandidatePayload
          : T extends PeerMessageType.PEER_ICE_COMPLETE
            ? PeerIceCompletePayload
            : T extends PeerMessageType.PEER_STATS
              ? PeerStatsPayload
              : T extends PeerMessageType.PEER_STATS_SUCCESS
                ? PeerStatsSuccessPayload
                : T extends PeerMessageType.PEER_ERROR
                  ? PeerErrorPayload
                  : never;

export function generatePeerMessage<T extends PeerMessageType>(type: T, payload: PayloadType<T>): PeerMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case PeerMessageType.PEER_GET_ID:
      return peerGetIdSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_GET_ID_ANSWER:
      return peerGetIdAnswerSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_SDP_OFFER:
      return peerSdpOfferSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_SDP_ANSWER:
      return peerSdpAnswerSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_ICE_CANDIDATE:
      return peerIceCandidateSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_ICE_COMPLETE:
      return peerIceCompleteSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_STATS:
      return peerStatsSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_STATS_SUCCESS:
      return peerStatsSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case PeerMessageType.PEER_ERROR:
      return peerErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
